/**
 * 商务风尚模版 - 详情页
 *
 * 设计理念：商务文章式详情 + 深色信息卡
 * 与 sunset（左右分屏 + 圆角渐变卡片）完全不同的排版骨架：
 * - 顶部：商务面包屑 + 金色装饰标题区
 * - 主体：左侧正文卡（方角 + 细边框）+ 图片竖排（flex flex-col）
 * - 右侧：深墨绿 header 信息卡（联系方式 + 公司名称受 hide_company_name 控制）
 * - 相关推荐：横向滚动商务卡
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/executive/components/Navbar";
import ExecutiveFooter from "@/templates/executive/components/Footer";
import { getListingDetail, getListings, type ListingDetail as ListingDetailType, type ListingItem } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function ExecutiveListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<ListingDetailType | null>(null);
  const [related, setRelated] = useState<ListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    window.scrollTo(0, 0);
    getListingDetail(id)
      .then((data) => {
        setListing(data);
        document.title = data?.title ? `${data.title} - 详情` : '详情';
        if (data?.category_id) {
          getListings({ category_id: data.category_id, page_size: 6 })
            .then(d => setRelated((d.list || []).filter(i => i.id !== id).slice(0, 5)))
            .catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6F4]">
        <Navbar />
        <div className="pt-8 animate-pulse">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-3/5 aspect-square bg-gray-200/70 rounded-md" />
              <div className="md:w-2/5 space-y-4">
                <div className="h-8 bg-gray-200/70 rounded-md w-3/4" />
                <div className="h-5 bg-gray-200/70 rounded-md w-1/2" />
                <div className="h-32 bg-gray-200/70 rounded-md" />
                <div className="h-24 bg-gray-200/70 rounded-md" />
              </div>
            </div>
          </div>
        </div>
        <ExecutiveFooter />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#F5F6F4]">
        <Navbar />
        <div className="pt-28 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-sm bg-[#1F4E3D]/5 border border-[#1F4E3D]/10">
            <i className="ri-error-warning-line text-3xl text-[#C2A04B]" />
          </div>
          <p className="text-gray-500 font-medium text-lg mb-2">信息不存在</p>
          <p className="text-gray-400 text-sm mb-6">该信息可能已被删除或下架</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] text-sm font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all shadow-md">
            <i className="ri-arrow-left-line" />返回首页
          </Link>
        </div>
        <ExecutiveFooter />
      </div>
    );
  }

  const images = listing.images || [];

  return (
    <div className="min-h-screen bg-[#F5F6F4]">
      <Navbar />

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* ======== 面包屑 ======== */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
            <Link to="/" className="hover:text-[#C2A04B] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-[#C2A04B] transition-colors">分类</Link>
            {listing.category?.name && (
              <>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/category/${listing.category_id}`} className="hover:text-[#C2A04B] transition-colors">
                  {listing.category.name}
                </Link>
              </>
            )}
          </nav>

          {/* ======== 标题区：金色装饰 ======== */}
          <div className="bg-white rounded-md border border-[#E2E8E0] p-6 sm:p-8 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-6 bg-[#C2A04B] rounded-full" />
              {listing.category?.name && (
                <span className="text-xs px-2.5 py-1 bg-[#1F4E3D]/5 text-[#1F4E3D] rounded-sm font-medium border border-[#1F4E3D]/10">
                  {listing.category.name}
                </span>
              )}
              {listing.is_pinned && (
                <span className="text-xs px-2.5 py-1 bg-[#C2A04B] text-[#1F4E3D] rounded-sm font-bold flex items-center gap-1">
                  <i className="ri-pushpin-line text-[10px]" />置顶
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#1F4E3D] leading-tight mb-4">
              {listing.title}
            </h1>
            <div className="flex items-center gap-4 flex-wrap text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-[#C2A04B]" />{formatDate(listing.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-[#C2A04B]" />{listing.view_count || 0} 次浏览
              </span>
              {listing.location && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-[#C2A04B]" />{listing.location}
                </span>
              )}
            </div>
          </div>

          {/* ======== 内容区域 ======== */}
          <div className="flex flex-col lg:flex-row gap-6">

            {/* ===== 左栏：正文 + 图片竖排 ===== */}
            <div className="lg:flex-1 min-w-0 space-y-6">

              {/* 操作按钮 */}
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-sm border border-[#E2E8E0] text-gray-500 hover:text-[#C2A04B] hover:border-[#C2A04B] hover:bg-white transition-all text-sm">
                  <i className="ri-share-forward-line" />分享
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-sm border border-[#E2E8E0] text-gray-500 hover:text-[#C2A04B] hover:border-[#C2A04B] hover:bg-white transition-all text-sm">
                  <i className="ri-heart-line" />收藏
                </button>
              </div>

              {/* 详细描述 */}
              <div className="bg-white rounded-md border border-[#E2E8E0] p-6 sm:p-8">
                <h3 className="text-base font-bold text-[#1F4E3D] mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C2A04B] rounded-full" />
                  详细描述
                </h3>
                {listing.content ? (
                  <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap break-words [&_a]:text-[#8C6E2E] [&_strong]:text-[#1F4E3D] [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-[#1F4E3D] [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1">
                    {listing.content}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">暂无详细描述</p>
                )}

                {/* 标签 */}
                {listing.tags && listing.tags.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-[#EDF0EC]">
                    <div className="flex flex-wrap gap-2">
                      {listing.tags.map((tag: string, i: number) => (
                        <span key={i} className="text-xs px-3 py-1.5 bg-[#1F4E3D]/5 text-[#1F4E3D] rounded-sm font-medium border border-[#1F4E3D]/10">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 图片竖排展示（flex flex-col，展示全部图片） */}
              {images.length > 0 && (
                <div className="flex flex-col gap-4">
                  {images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={listing.title}
                      className="w-full h-auto rounded-md cursor-pointer hover:opacity-95 transition-opacity border border-[#E2E8E0]"
                      onClick={() => { setCurrentImage(i); setLightboxOpen(true); }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ===== 右栏：深色信息卡 ===== */}
            <div className="lg:w-80 shrink-0">
              <div className="lg:sticky lg:top-24 space-y-5">

                {/* 发布者信息卡片：深墨绿 header */}
                <div className="bg-white rounded-md border border-[#E2E8E0] overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3.5 bg-[#1F4E3D]">
                    <i className="ri-customer-service-2-line text-[#DDC583]" />
                    <span className="text-sm font-bold text-white">信息详情</span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-md border border-[#DDC583]/40">
                        {listing.contact_name ? listing.contact_name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800">{listing.contact_name || "匿名用户"}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{formatDate(listing.created_at)} 发布</div>
                      </div>
                    </div>

                    {/* 公司名称：仅当后台未开启"隐藏公司名称"时显示（siteConfig 加载完成后再渲染，避免配置未知时误显示） */}
                    {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                      <div className="mb-4 p-3 bg-[#1F4E3D]/5 rounded-sm border border-[#1F4E3D]/10">
                        <span className="text-xs text-gray-400">公司名称</span>
                        <p className="text-sm font-medium text-gray-700 mt-0.5">{listing.company_name}</p>
                      </div>
                    )}

                    {/* 联系方式列表 */}
                    <div className="space-y-2.5">
                      {listing.contact_phone && (
                        <a
                          href={`tel:${listing.contact_phone}`}
                          className="flex items-center gap-3 px-4 py-3 bg-[#F7F8F6] rounded-sm hover:bg-[#1F4E3D]/5 transition-colors group border border-[#EDF0EC]"
                        >
                          <div className="w-9 h-9 rounded-sm bg-[#1F4E3D] flex items-center justify-center shrink-0">
                            <i className="ri-phone-line text-[#DDC583]" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">联系电话</div>
                            <div className="text-sm font-semibold text-gray-800 group-hover:text-[#1F4E3D] transition-colors">
                              {listing.contact_phone}
                            </div>
                          </div>
                        </a>
                      )}
                      {listing.contact_wechat && (
                        <div className="flex items-center gap-3 px-4 py-3 bg-[#F7F8F6] rounded-sm border border-[#EDF0EC]">
                          <div className="w-9 h-9 rounded-sm bg-[#1F4E3D] flex items-center justify-center shrink-0">
                            <i className="ri-wechat-line text-[#DDC583]" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">微信</div>
                            <div className="text-sm font-semibold text-gray-800">{listing.contact_wechat}</div>
                          </div>
                        </div>
                      )}
                      {listing.contact_email && (
                        <a
                          href={`mailto:${listing.contact_email}`}
                          className="flex items-center gap-3 px-4 py-3 bg-[#F7F8F6] rounded-sm hover:bg-[#1F4E3D]/5 transition-colors group border border-[#EDF0EC]"
                        >
                          <div className="w-9 h-9 rounded-sm bg-[#1F4E3D] flex items-center justify-center shrink-0">
                            <i className="ri-mail-line text-[#DDC583]" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">电子邮箱</div>
                            <div className="text-sm font-semibold text-gray-800 group-hover:text-[#1F4E3D] transition-colors">
                              {listing.contact_email}
                            </div>
                          </div>
                        </a>
                      )}
                      {!listing.contact_phone && !listing.contact_wechat && !listing.contact_email && (
                        <p className="text-gray-400 text-sm text-center py-3">暂未提供联系方式</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 快速操作 */}
                <div className="bg-white rounded-md border border-[#E2E8E0] p-5 space-y-3">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <i className="ri-flashlight-line text-[#C2A04B]" />快速操作
                  </h4>
                  <Link
                    to="/publish"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all text-sm shadow-md shadow-[#C2A04B]/20"
                  >
                    <i className="ri-add-line" />发布信息
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ======== 相关推荐：横向滚动 ======== */}
          {related.length > 0 && (
            <section className="mt-12">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1 h-6 bg-[#C2A04B] rounded-full" />
                <h2 className="text-lg font-black text-[#1F4E3D]">相关推荐</h2>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="shrink-0 w-48 sm:w-56 group"
                  >
                    <div className="bg-white rounded-md border border-[#E2E8E0] overflow-hidden hover:shadow-lg hover:border-[#C2A04B] transition-all">
                      <div className="aspect-square bg-[#EDF0EC] overflow-hidden">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-line text-[#C2A04B]/50 text-2xl" />
                          </div>
                        )}
                      </div>
                      <div className="p-3.5">
                        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-[#1F4E3D] transition-colors">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span className="text-[11px] px-2 py-0.5 bg-[#1F4E3D]/5 text-[#1F4E3D] rounded-sm border border-[#1F4E3D]/10">
                            {item.category?.name || "未分类"}
                          </span>
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

        </div>
      </div>

      {/* ======== 图片灯箱 ======== */}
      {lightboxOpen && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-sm bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage - 1 + images.length) % images.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-sm bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}
          <img
            src={images[currentImage]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage + 1) % images.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-sm bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <i className="ri-arrow-right-s-line text-2xl" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentImage(i); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === currentImage ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ======== 移动端底部浮动联系按钮 ======== */}
      {listing.contact_phone && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/95 backdrop-blur-md border-t border-[#E2E8E0] px-4 py-3 z-40">
          <div className="flex items-center gap-3">
            <a
              href={`tel:${listing.contact_phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-bold rounded-sm shadow-lg shadow-[#C2A04B]/30"
            >
              <i className="ri-phone-line text-lg" />拨打电话
            </a>
            {listing.contact_wechat && (
              <button className="shrink-0 w-12 h-12 rounded-sm bg-[#1F4E3D] flex items-center justify-center text-[#DDC583]">
                <i className="ri-wechat-line text-lg" />
              </button>
            )}
          </div>
        </div>
      )}

      <ExecutiveFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
